import type { Problem } from '@/types';

/* Practice Gym ladder: Retention & Cohort Analysis (module m10). Problem-forward, progressive.
   Auto-included via src/data/gym/index.ts; referenced by the `ret` ladder. */
export const retentionProblems: Problem[] = [
  {
    "id": "rt1",
    "ladder": "ret",
    "pos": 1,
    "stage": "Recognition",
    "lvl": 0,
    "difficulty": "recognition",
    "priority": "required",
    "source": "interview-style",
    "module": "m10",
    "title": "What is a cohort, and what's the denominator?",
    "concept": ["retention"],
    "obj": ["Customer", "Charge"],
    "metric": "n/a",
    "edge": ["cohort key"],
    "timed": false,
    "est": "3 min",
    "business": "A PM asks for 'customer retention' but it's unclear which start and which activity.",
    "schema": ["customers", "charges"],
    "prompt": "No SQL yet. In plain English: define a cohort, name the retention denominator, and state the difference between a signup cohort and a first-activity cohort.",
    "deliverable": "A short explanation: cohort, denominator (cohort size), and the two anchors.",
    "why": "Retention is ambiguous until you fix the cohort anchor and the activity rule; recognise this before any SQL.",
    "harder": "First rung — name the cohort, the fixed denominator, and the two anchors.",
    "before": [
      "A cohort fixes the START and follows that fixed group over time.",
      "Why a customer who signs up but never charges differs between the two anchors."
    ],
    "hints": [
      "Cohort size is the denominator, fixed across all offsets.",
      "Signup cohort keys on customers.created_at; first-activity keys on the first succeeded charge."
    ],
    "model": "A cohort is a group sharing a start event in a period — e.g. customers who signed up in March. Retention(n) = distinct cohort members active n months later ÷ cohort size, where cohort size is the fixed denominator. A signup cohort keys on customers.created_at; a first-activity cohort keys on each customer's first succeeded charge — a sign-up-but-never-paid customer is in the first but not the second.",
    "grain": "Conceptual — no output rows.",
    "confusion": "The cohort denominator is fixed once; it must not change across offsets.",
    "explain": "Name the cohort, its fixed denominator, and the two anchors aloud.",
    "teaches": "Recognising cohorts, the retention denominator, and anchor choice.",
    "mode": "SQL"
  },
  {
    "id": "rt2",
    "ladder": "ret",
    "pos": 2,
    "stage": "Mechanical",
    "lvl": 1,
    "difficulty": "easy",
    "priority": "required",
    "source": "pgexercises-style",
    "module": "m10",
    "title": "Assign signup cohorts and sizes",
    "concept": ["retention"],
    "obj": ["Customer"],
    "metric": "count",
    "edge": [],
    "timed": false,
    "est": "4 min",
    "business": "You need each signup-month cohort and how many customers it contains.",
    "schema": ["customers"],
    "prompt": "Return one row per signup-month cohort with the count of customers.",
    "deliverable": "One row per cohort month: cohort_month, cohort_size.",
    "why": "The cohort key and size are the foundation every retention number divides by.",
    "harder": "Introduces the cohort key (DATE_TRUNC on signup) and distinct count.",
    "before": ["Cohort key = DATE_TRUNC('month', customers.created_at)."],
    "hints": [
      "GROUP BY DATE_TRUNC('month', created_at).",
      "COUNT(DISTINCT customer_id) for the size."
    ],
    "solution": "SELECT DATE_TRUNC('month', created_at) AS cohort_month,\n       COUNT(DISTINCT customer_id) AS cohort_size\nFROM customers\nGROUP BY DATE_TRUNC('month', created_at)\nORDER BY cohort_month;",
    "grain": "One row per signup-month cohort.",
    "confusion": "The cohort is keyed on signup, not on any later activity.",
    "explain": "State that the cohort is the signup month and the size is distinct customers.",
    "teaches": "Defining a signup cohort and its size.",
    "mode": "SQL"
  },
  {
    "id": "rt3",
    "ladder": "ret",
    "pos": 3,
    "stage": "Simple application",
    "lvl": 2,
    "difficulty": "medium",
    "priority": "required",
    "source": "Mode-style",
    "module": "m10",
    "title": "Month-1 retention by cohort",
    "concept": ["retention"],
    "obj": ["Customer", "Charge"],
    "metric": "retention",
    "edge": ["distinct", "month offset"],
    "timed": false,
    "est": "7 min",
    "business": "Growth wants month-1 retention: of each signup cohort, how many were active the next month.",
    "schema": ["customers", "charges"],
    "prompt": "Per signup cohort, return cohort_size, retained_m1 (distinct customers with a succeeded charge in the month after signup), and the month-1 retention rate.",
    "deliverable": "One row per cohort: cohort_month, cohort_size, retained_m1, retention_m1.",
    "why": "Month-1 retention is the headline activation metric; the offset match and distinct counting are the skill.",
    "harder": "Joins activity to the cohort at a one-month offset and counts distinct.",
    "before": [
      "Activity = distinct (customer, active_month) of succeeded charges.",
      "Match active_month = cohort_month + INTERVAL '1 month'; count distinct customers."
    ],
    "howto": [
      "cohort CTE from customers.",
      "activity CTE: distinct customer + active month of succeeded charges.",
      "LEFT JOIN, FILTER the one-month offset, divide by cohort size."
    ],
    "hints": [
      "COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '1 month').",
      "Guard the rate with NULLIF(cohort_size,0)."
    ],
    "solution": "WITH cohort AS (\n  SELECT customer_id, DATE_TRUNC('month', created_at) AS cohort_month FROM customers\n),\nactivity AS (\n  SELECT DISTINCT customer_id, DATE_TRUNC('month', created_at) AS active_month\n  FROM charges WHERE status='succeeded'\n)\nSELECT c.cohort_month,\n       COUNT(DISTINCT c.customer_id) AS cohort_size,\n       COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '1 month') AS retained_m1,\n       ROUND(COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '1 month')::numeric\n             / NULLIF(COUNT(DISTINCT c.customer_id),0), 4) AS retention_m1\nFROM cohort c\nLEFT JOIN activity a USING (customer_id)\nGROUP BY c.cohort_month\nORDER BY c.cohort_month;",
    "verify": {
      "grain": "One row per signup cohort.",
      "columns": ["cohort_month", "cohort_size", "retained_m1", "retention_m1"],
      "sample": { "cols": ["cohort_month", "cohort_size", "retained_m1", "retention_m1"], "rows": [["2024-01-01", "820", "402", "0.4902"], ["2024-02-01", "910", "455", "0.5000"]] },
      "commonWrong": ["Counting charge events (retained > cohort).", "Keying the cohort on the activity month."],
      "validation": ["retained_m1 ≤ cohort_size", "retention_m1 ∈ [0,1]"],
      "edgeCases": ["A cohort member active in month 1 with several charges counts once.", "The newest cohort's month-1 may be partial."],
      "checklist": ["cohort from signup", "distinct activity", "one-month offset", "guarded rate"]
    },
    "confusion": "Match the activity month to the cohort + 1 month, counting distinct customers.",
    "explain": "State the offset match and distinct counting before writing.",
    "teaches": "Month-1 retention with a distinct-entity offset match.",
    "mode": "SQL"
  },
  {
    "id": "rt4",
    "ladder": "ret",
    "pos": 4,
    "stage": "Applied metric",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "required",
    "source": "DataLemur-style",
    "module": "m10",
    "title": "Retention curve m0–m3",
    "concept": ["retention"],
    "obj": ["Customer", "Charge"],
    "metric": "retention curve",
    "edge": ["immature cohorts", "month offset"],
    "timed": false,
    "est": "9 min",
    "business": "Plot the retention curve: per cohort, the rate at offsets 0, 1, 2 and 3.",
    "schema": ["customers", "charges"],
    "prompt": "Per signup cohort, return cohort_size and the retention rate at month offsets 0, 1, 2 and 3.",
    "deliverable": "One row per cohort: cohort_month, cohort_size, m0, m1, m2, m3.",
    "why": "The retention curve is how survival is read; one FILTER per offset over a fixed denominator is the pattern.",
    "harder": "Repeats the offset match for four offsets while keeping one denominator.",
    "before": ["Each offset is a separate FILTER on active_month = cohort_month + INTERVAL 'n months'.", "Divide every offset by the same cohort_size."],
    "hints": [
      "Four FILTER expressions, offsets 0..3.",
      "The newest cohorts' far offsets will be 0/immature — that's expected here."
    ],
    "solution": "WITH cohort AS (\n  SELECT customer_id, DATE_TRUNC('month', created_at) AS cohort_month FROM customers\n),\nactivity AS (\n  SELECT DISTINCT customer_id, DATE_TRUNC('month', created_at) AS active_month\n  FROM charges WHERE status='succeeded'\n)\nSELECT c.cohort_month,\n       COUNT(DISTINCT c.customer_id) AS cohort_size,\n       ROUND(COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month)::numeric / NULLIF(COUNT(DISTINCT c.customer_id),0),4) AS m0,\n       ROUND(COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '1 month')::numeric / NULLIF(COUNT(DISTINCT c.customer_id),0),4) AS m1,\n       ROUND(COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '2 months')::numeric / NULLIF(COUNT(DISTINCT c.customer_id),0),4) AS m2,\n       ROUND(COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '3 months')::numeric / NULLIF(COUNT(DISTINCT c.customer_id),0),4) AS m3\nFROM cohort c\nLEFT JOIN activity a USING (customer_id)\nGROUP BY c.cohort_month\nORDER BY c.cohort_month;",
    "verify": {
      "grain": "One row per cohort with four offset rates.",
      "columns": ["cohort_month", "cohort_size", "m0", "m1", "m2", "m3"],
      "commonWrong": ["Letting the denominator differ per offset.", "Counting events."],
      "validation": ["every rate ∈ [0,1]", "rates generally non-increasing across offsets (with reactivation exceptions)"],
      "edgeCases": ["Newest cohorts: m2/m3 are immature.", "Reactivation can make a later offset exceed an earlier one."],
      "checklist": ["fixed denominator", "four offsets", "distinct counts", "guards"]
    },
    "confusion": "Hold the denominator (cohort_size) constant across all four offsets.",
    "explain": "Explain that each offset divides by the same cohort size.",
    "teaches": "A multi-offset retention curve.",
    "mode": "SQL"
  },
  {
    "id": "rt5",
    "ladder": "ret",
    "pos": 5,
    "stage": "Multi-step",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "should",
    "source": "Mode-style",
    "module": "m10",
    "title": "First-activity cohort vs signup cohort",
    "concept": ["retention"],
    "obj": ["Customer", "Charge"],
    "metric": "retention",
    "edge": ["cohort anchor"],
    "timed": false,
    "est": "9 min",
    "business": "Product wants retention anchored on first activity, not signup, to exclude never-payers.",
    "schema": ["charges"],
    "prompt": "Build a first-activity cohort (month of each customer's first succeeded charge) and report month-1 retention. Note how the cohort differs from a signup cohort.",
    "deliverable": "One row per first-activity cohort: cohort_month, cohort_size, retention_m1.",
    "why": "Choosing the anchor changes who's in the denominator; first-activity cohorts are common for activated-user retention.",
    "harder": "Derives the cohort from an aggregate (MIN first charge) instead of a base table.",
    "before": ["first_activity = month of MIN(created_at) over succeeded charges per customer.", "Never-payers are absent from this cohort."],
    "howto": [
      "first_activity CTE: per customer, DATE_TRUNC('month', MIN(succeeded created_at)).",
      "activity CTE: distinct customer + active month.",
      "LEFT JOIN, one-month offset, distinct count over the first-activity size."
    ],
    "hints": [
      "GROUP charges BY customer_id, take MIN(created_at) of succeeded.",
      "The denominator is first-activity customers, smaller than signup customers."
    ],
    "solution": "WITH first_activity AS (\n  SELECT customer_id, DATE_TRUNC('month', MIN(created_at)) AS cohort_month\n  FROM charges WHERE status='succeeded'\n  GROUP BY customer_id\n),\nactivity AS (\n  SELECT DISTINCT customer_id, DATE_TRUNC('month', created_at) AS active_month\n  FROM charges WHERE status='succeeded'\n)\nSELECT f.cohort_month,\n       COUNT(DISTINCT f.customer_id) AS cohort_size,\n       ROUND(COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = f.cohort_month + INTERVAL '1 month')::numeric\n             / NULLIF(COUNT(DISTINCT f.customer_id),0), 4) AS retention_m1\nFROM first_activity f\nLEFT JOIN activity a USING (customer_id)\nGROUP BY f.cohort_month\nORDER BY f.cohort_month;",
    "verify": {
      "grain": "One row per first-activity cohort.",
      "columns": ["cohort_month", "cohort_size", "retention_m1"],
      "commonWrong": ["Including never-payers (they have no first activity).", "Keying on signup instead of first charge."],
      "validation": ["cohort_size ≤ number of customers who ever charged", "retention_m1 ∈ [0,1]"],
      "edgeCases": ["A customer whose first charge is their only charge → retained_m1 false.", "Refunded first charge still counts as activity if succeeded."],
      "checklist": ["first-activity anchor", "MIN succeeded per customer", "distinct activity", "guarded rate"]
    },
    "confusion": "First-activity excludes sign-ups who never paid — the denominator is smaller and the rate usually higher.",
    "explain": "Contrast the two anchors and which customers each includes.",
    "teaches": "Anchoring a cohort on first activity.",
    "mode": "SQL"
  },
  {
    "id": "rt6",
    "ladder": "ret",
    "pos": 6,
    "stage": "Debug",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "should",
    "source": "interview-style",
    "module": "m10",
    "title": "Debug: cohort leakage",
    "concept": ["retention"],
    "obj": ["Customer", "Charge"],
    "metric": "cohort size",
    "edge": ["cohort leakage"],
    "timed": false,
    "est": "6 min",
    "business": "A 'signup cohort' report has cohort sizes that grow and shrink month to month — the same customer keeps moving cohorts.",
    "schema": ["customers", "charges"],
    "prompt": "Diagnose the bug in one sentence, then fix it so the cohort is fixed by signup.",
    "broken": "SELECT DATE_TRUNC('month', ch.created_at) AS cohort_month,\n       COUNT(DISTINCT ch.customer_id) AS cohort_size\nFROM charges ch\nWHERE ch.status='succeeded'\nGROUP BY DATE_TRUNC('month', ch.created_at);",
    "deliverable": "A one-sentence diagnosis + a corrected signup-keyed cohort query.",
    "why": "Cohort leakage is the defining retention bug; recognising it is essential.",
    "harder": "You must see that the cohort key is the activity date, not the signup date, and re-anchor.",
    "before": ["A cohort must be fixed by the start event, once per customer."],
    "hints": [
      "Keying on the charge month means a customer joins a new cohort every active month.",
      "Anchor on customers.created_at instead."
    ],
    "solution": "WITH cohort AS (\n  SELECT customer_id, DATE_TRUNC('month', created_at) AS cohort_month\n  FROM customers\n)\nSELECT cohort_month, COUNT(DISTINCT customer_id) AS cohort_size\nFROM cohort\nGROUP BY cohort_month\nORDER BY cohort_month;",
    "verify": {
      "grain": "One row per signup cohort.",
      "columns": ["cohort_month", "cohort_size"],
      "commonWrong": ["Keeping the charge-month key but adding DISTINCT (still leakage).", "Using MAX instead of the signup date."],
      "validation": ["A given customer appears in exactly one cohort", "cohort sizes sum to total customers"],
      "edgeCases": ["A customer with charges in three months must still belong to one signup cohort."],
      "checklist": ["named leakage", "anchored on signup", "one cohort per customer"]
    },
    "confusion": "The fix is the anchor (signup), not adding DISTINCT to the activity-keyed query.",
    "explain": "Name cohort leakage before re-anchoring on signup.",
    "teaches": "Fixing cohort leakage by anchoring on the start event.",
    "mode": "SQL"
  },
  {
    "id": "rt7",
    "ladder": "ret",
    "pos": 7,
    "stage": "Edge-case hard",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "stretch",
    "source": "StrataScratch-style",
    "module": "m10",
    "title": "Exclude immature cohorts from month-3 retention",
    "concept": ["retention"],
    "obj": ["Customer", "Charge"],
    "metric": "retention",
    "edge": ["immature cohorts"],
    "timed": false,
    "est": "9 min",
    "business": "A month-3 retention report shows recent cohorts at ~0%, scaring leadership — but those cohorts are too new to have a month 3.",
    "schema": ["customers", "charges"],
    "prompt": "Report month-3 retention by signup cohort, but only for cohorts old enough to have reached month 3. State the caveat.",
    "deliverable": "One row per mature cohort: cohort_month, cohort_size, retention_m3.",
    "why": "Excluding immature cohorts is the judgment that prevents a fake churn cliff in the data.",
    "harder": "You must filter out cohorts whose month-3 lies in the future, not zero-fill them.",
    "before": ["A cohort is mature for month 3 only if cohort_month + 3 months has already passed.", "Excluding immature cohorts is correct; zero-filling is misleading."],
    "hints": [
      "WHERE cohort_month + INTERVAL '3 months' <= DATE_TRUNC('month', NOW()).",
      "Distinct counts and a guarded rate as usual."
    ],
    "solution": "WITH cohort AS (\n  SELECT customer_id, DATE_TRUNC('month', created_at) AS cohort_month FROM customers\n),\nactivity AS (\n  SELECT DISTINCT customer_id, DATE_TRUNC('month', created_at) AS active_month\n  FROM charges WHERE status='succeeded'\n)\nSELECT c.cohort_month,\n       COUNT(DISTINCT c.customer_id) AS cohort_size,\n       ROUND(COUNT(DISTINCT a.customer_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '3 months')::numeric\n             / NULLIF(COUNT(DISTINCT c.customer_id),0), 4) AS retention_m3\nFROM cohort c\nLEFT JOIN activity a USING (customer_id)\nWHERE c.cohort_month + INTERVAL '3 months' <= DATE_TRUNC('month', NOW())\nGROUP BY c.cohort_month\nORDER BY c.cohort_month;",
    "verify": {
      "grain": "One row per mature cohort.",
      "columns": ["cohort_month", "cohort_size", "retention_m3"],
      "commonWrong": ["Including immature cohorts and reading their 0 as churn.", "Filtering with NOW() instead of DATE_TRUNC('month', NOW()), leaking the current partial month."],
      "validation": ["No cohort newer than 3 months appears", "retention_m3 ∈ [0,1]"],
      "edgeCases": ["The cohort exactly 3 months old is included.", "Time zone affects which month is 'now'."],
      "checklist": ["maturity filter", "month-truncated NOW()", "distinct counts", "caveat stated"]
    },
    "confusion": "Exclude immature cohorts; never read an absent month-3 as 0% retention.",
    "explain": "Justify the maturity filter and the immature-data caveat aloud.",
    "teaches": "Guarding a retention report against immature cohorts.",
    "mode": "SQL"
  },
  {
    "id": "rt8",
    "ladder": "ret",
    "pos": 8,
    "stage": "Final boss",
    "lvl": 5,
    "difficulty": "final-boss",
    "priority": "boss",
    "source": "Stripe-interview-style",
    "module": "m10",
    "title": "Final boss (timed 12 min): merchant retention + churn",
    "concept": ["retention"],
    "obj": ["Merchant", "Charge"],
    "metric": "retention + churn",
    "edge": ["immature cohorts", "distinct", "anchor"],
    "timed": true,
    "est": "12 min",
    "business": "\"Are newer merchant cohorts retaining worse?\" Build the cohort retention with churn and defend every choice.",
    "schema": ["merchants", "charges"],
    "prompt": "Per merchant signup cohort (by merchants.created_at), report cohort_size, month-1 and month-3 retention (active = succeeded charge that month), and month-3 churn = 1 − retention. Exclude cohorts too new for month 3 and state your anchor, activity rule, and immaturity caveat.",
    "deliverable": "One row per mature cohort: cohort_month, cohort_size, retention_m1, retention_m3, churn_m3.",
    "why": "This fuses anchoring, offset arithmetic, distinct counting, churn, and maturity judgment — a complete cohort interview answer.",
    "harder": "Combines two offsets, churn, a maturity exclusion and distinct-merchant counting under time pressure.",
    "before": [
      "Anchor on merchants.created_at; activity = distinct (merchant, active_month) of succeeded charges.",
      "Exclude cohorts where cohort_month + 3 months is still in the future.",
      "churn = 1 − retention_m3; guard every divide."
    ],
    "howto": [
      "cohort CTE from merchants; activity CTE distinct merchant + month.",
      "Two FILTER offsets (1 and 3); churn from the m3 rate.",
      "WHERE the cohort is mature for month 3; GROUP BY cohort."
    ],
    "hints": [
      "COUNT(DISTINCT merchant_id) for size and each offset.",
      "Maturity: cohort_month + INTERVAL '3 months' <= DATE_TRUNC('month', NOW()).",
      "churn = ROUND(1 - retention_m3, 4)."
    ],
    "solution": "WITH cohort AS (\n  SELECT merchant_id, DATE_TRUNC('month', created_at) AS cohort_month FROM merchants\n),\nactivity AS (\n  SELECT DISTINCT merchant_id, DATE_TRUNC('month', created_at) AS active_month\n  FROM charges WHERE status='succeeded'\n)\nSELECT c.cohort_month,\n       COUNT(DISTINCT c.merchant_id) AS cohort_size,\n       ROUND(COUNT(DISTINCT a.merchant_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '1 month')::numeric\n             / NULLIF(COUNT(DISTINCT c.merchant_id),0), 4) AS retention_m1,\n       ROUND(COUNT(DISTINCT a.merchant_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '3 months')::numeric\n             / NULLIF(COUNT(DISTINCT c.merchant_id),0), 4) AS retention_m3,\n       ROUND(1 - COUNT(DISTINCT a.merchant_id) FILTER (WHERE a.active_month = c.cohort_month + INTERVAL '3 months')::numeric\n             / NULLIF(COUNT(DISTINCT c.merchant_id),0), 4) AS churn_m3\nFROM cohort c\nLEFT JOIN activity a USING (merchant_id)\nWHERE c.cohort_month + INTERVAL '3 months' <= DATE_TRUNC('month', NOW())\nGROUP BY c.cohort_month\nORDER BY c.cohort_month;",
    "rubric": [
      "Cohort anchored on merchants.created_at, stated",
      "Activity = distinct merchants with a succeeded charge in the offset month",
      "Fixed cohort-size denominator across offsets",
      "Immature cohorts excluded (not zero-filled)",
      "churn = 1 − retention_m3, guards on every rate",
      "Distinct merchants counted, not charge events"
    ],
    "verify": {
      "grain": "One row per mature merchant cohort.",
      "columns": ["cohort_month", "cohort_size", "retention_m1", "retention_m3", "churn_m3"],
      "sample": { "cols": ["cohort_month", "cohort_size", "retention_m1", "retention_m3", "churn_m3"], "rows": [["2023-09-01", "120", "0.7500", "0.6000", "0.4000"], ["2023-10-01", "140", "0.7200", "0.5500", "0.4500"]] },
      "commonWrong": ["Counting charge events.", "Including immature cohorts.", "Cohort key on activity month."],
      "validation": ["rates ∈ [0,1]", "churn_m3 = 1 − retention_m3", "no cohort newer than 3 months"],
      "edgeCases": ["Reactivation: active at m3 but not m1.", "Time-zone shifts the maturity boundary."],
      "checklist": ["signup anchor", "distinct merchants", "two offsets", "maturity filter", "churn derived"]
    },
    "confusion": "Hold the denominator fixed, count distinct merchants, and exclude immature cohorts rather than zero-filling.",
    "explain": "Defend the anchor, the activity rule, the maturity filter and churn as if to an interviewer.",
    "teaches": "A complete merchant cohort retention + churn report.",
    "mode": "SQL"
  }
];
